import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { EXO_DRIVERS, DRIVERS_ES } from "@/data/driversExo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
const DRIVER_BY_KEY = Object.fromEntries(EXO_DRIVERS.map((d) => [d.key, d]));
const DEFAULT_RANKING = EXO_DRIVERS.map((d) => d.key);

const T = {
  fr: {
    badge: "Regard d'un proche",
    introTitle: "Comment percevez-vous ses « drivers » relationnels\u00a0?",
    intro1: "Une personne proche de vous a réalisé un travail sur ses « drivers relationnels » — ces messages intérieurs (Sois parfait, Fais plaisir, Sois fort…) qui guident nos comportements. Elle souhaite recueillir votre regard.",
    intro2: "Pour chaque driver, vous découvrirez sa définition, puis vous décrirez comment vous le percevez chez cette personne. Vous établirez enfin votre propre classement. Vos réponses lui seront transmises ; seul(e) elle verra la synthèse.",
    start: "Commencer",
    driverOf: (i, t) => `Driver ${i} sur ${t}`,
    driver: "Driver",
    injonction: "Injonction interne",
    messages: "Messages internes",
    positifs: "Points positifs",
    negatifs: "Points négatifs",
    yourView: "Votre regard",
    yourViewHint: "Décrivez, avec bienveillance, comment vous percevez ce driver chez cette personne.",
    prev: "Précédent",
    next: "Suivant",
    toRanking: "Passer au classement",
    placeholder: "Une ou plusieurs situations…",
    rankTitle: "Votre classement",
    rankHint: "Selon vous, classez ces cinq drivers du plus présent / important au moins présent chez cette personne.",
    nameLabel: "Votre prénom (facultatif)",
    namePh: "Votre prénom",
    submit: "Transmettre mon regard",
    sending: "Envoi…",
    thanks: (n) => `Merci${n ? `, ${n}` : ""} !`,
    thanksText: "Votre regard a bien été transmis. La personne qui vous a invité(e) pourra le découvrir et le comparer avec son propre travail.",
    respondAgain: "Répondre pour une autre personne",
    close: "Vous pouvez maintenant fermer cette page.",
    invalidTitle: "Lien invalide",
    invalidText: "Ce lien n'est pas valide ou a expiré. Merci de redemander un lien à la personne qui vous a invité(e).",
    loading: "Chargement…",
    cases: [
      { key: "utile", label: "Situations où ce driver lui a été utile (selon vous)" },
      { key: "pas_utile", label: "Situations où ce driver ne lui a pas été utile (selon vous)" },
      { key: "aurait_utile", label: "Situations où ce driver lui aurait été utile (selon vous)" },
    ],
  },
  es: {
    badge: "La mirada de un allegado",
    introTitle: "¿Cómo percibe sus «drivers» relacionales?",
    intro1: "Una persona cercana a usted ha realizado un trabajo sobre sus «drivers relacionales» — esos mensajes internos (Sé perfecto, Complace, Sé fuerte…) que guían nuestros comportamientos. Desea recoger su mirada.",
    intro2: "Para cada driver, descubrirá su definición y luego describirá cómo lo percibe en esta persona. Por último, establecerá su propia clasificación. Sus respuestas le serán transmitidas; solo ella verá la síntesis.",
    start: "Empezar",
    driverOf: (i, t) => `Driver ${i} de ${t}`,
    driver: "Driver",
    injonction: "Mandato interno",
    messages: "Mensajes internos",
    positifs: "Puntos positivos",
    negatifs: "Puntos negativos",
    yourView: "Su mirada",
    yourViewHint: "Describa, con benevolencia, cómo percibe este driver en esta persona.",
    prev: "Anterior",
    next: "Siguiente",
    toRanking: "Pasar a la clasificación",
    placeholder: "Una o varias situaciones…",
    rankTitle: "Su clasificación",
    rankHint: "Según usted, clasifique estos cinco drivers del más presente / importante al menos presente en esta persona.",
    nameLabel: "Su nombre (opcional)",
    namePh: "Su nombre",
    submit: "Transmitir mi mirada",
    sending: "Enviando…",
    thanks: (n) => `¡Gracias${n ? `, ${n}` : ""}!`,
    thanksText: "Su mirada se ha transmitido correctamente. La persona que le invitó podrá descubrirla y compararla con su propio trabajo.",
    respondAgain: "Responder por otra persona",
    close: "Ahora puede cerrar esta página.",
    invalidTitle: "Enlace no válido",
    invalidText: "Este enlace no es válido o ha caducado. Solicite un nuevo enlace a la persona que le invitó.",
    loading: "Cargando…",
    cases: [
      { key: "utile", label: "Situaciones en las que este driver le fue útil (según usted)" },
      { key: "pas_utile", label: "Situaciones en las que este driver no le fue útil (según usted)" },
      { key: "aurait_utile", label: "Situaciones en las que este driver le habría sido útil (según usted)" },
    ],
  },
};

const driverContent = (driver, lang) => {
  if (lang === "es" && DRIVERS_ES[driver.key]) return { ...driver, ...DRIVERS_ES[driver.key] };
  return driver;
};

const freshEntries = () =>
  Object.fromEntries(EXO_DRIVERS.map((d) => [d.key, { utile: "", pas_utile: "", aurait_utile: "" }]));

const Primary = ({ children, ...p }) => (
  <button {...p} className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">{children}</button>
);
const Ghost = ({ children, ...p }) => (
  <button {...p} className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{children}</button>
);

const BulletBlock = ({ title, items, color }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.12em] mb-1" style={{ color }}>{title}</p>
    <ul className="list-disc pl-5 space-y-0.5 text-sm text-[var(--ink)]">
      {items.map((it) => (<li key={it}>{it}</li>))}
    </ul>
  </div>
);

const Intro = ({ t, onStart }) => (
  <div className="animate-fade-up text-center">
    <p className="text-xs uppercase tracking-[0.3em] text-[var(--gold-light)] mb-4">{t.badge}</p>
    <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] leading-tight mb-6">{t.introTitle}</h1>
    <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto mb-4">{t.intro1}</p>
    <p className="text-[var(--ink-soft)] text-sm max-w-xl mx-auto mb-8">{t.intro2}</p>
    <Primary onClick={onStart} data-testid="entourage-btn-start">{t.start}</Primary>
  </div>
);

const DriverStep = ({ t, lang, driver, index, total, values, onChange, onPrev, onNext }) => {
  const c = driverContent(driver, lang);
  return (
    <div className="animate-fade-up">
      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm uppercase tracking-[0.18em] text-[var(--ink-soft)]">{t.driverOf(index + 1, total)}</span>
          <span className="font-serif-title text-2xl text-[var(--gold)]">{Math.round(((index + 1) / total) * 100)} %</span>
        </div>
        <div className="h-3 w-full rounded-full bg-[var(--beige)] overflow-hidden">
          <div className="h-full rounded-full bg-[var(--gold)] transition-all duration-500" style={{ width: `${((index + 1) / total) * 100}%` }} />
        </div>
      </div>

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6" data-testid={`entourage-driver-${driver.key}`}>
        <img src={`/drivers/${driver.key}.jpg`} alt="" loading="lazy" className="w-full h-40 sm:h-56 object-cover rounded-2xl mb-5" data-testid={`entourage-driver-img-${driver.key}`} />
        <h2 className="font-serif-title text-3xl sm:text-4xl mb-1" style={{ color: driver.color }}>{t.driver} : {c.name}</h2>
        <p className="font-serif-title text-xl text-[var(--ink)] italic mb-5">{c.slogan}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <BulletBlock title={t.injonction} items={c.injonction} color={driver.color} />
          <BulletBlock title={t.messages} items={c.messages} color={driver.color} />
          <BulletBlock title={t.positifs} items={c.positifs} color={driver.color} />
          <BulletBlock title={t.negatifs} items={c.negatifs} color={driver.color} />
        </div>
      </div>

      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-6">
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">{t.yourView}</h3>
        <p className="text-sm text-[var(--ink-soft)] mb-5">{t.yourViewHint}</p>
        <div className="space-y-5">
          {t.cases.map((cc) => (
            <div key={cc.key}>
              <label className="block text-sm font-semibold text-[var(--ink)] mb-2">{cc.label}</label>
              <textarea
                rows={3}
                value={values[cc.key]}
                onChange={(e) => onChange(cc.key, e.target.value)}
                data-testid={`entourage-text-${driver.key}-${cc.key}`}
                placeholder={t.placeholder}
                className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-8">
        <Ghost onClick={onPrev} data-testid="entourage-btn-prev">{t.prev}</Ghost>
        <Primary onClick={onNext} data-testid="entourage-btn-next">{index === total - 1 ? t.toRanking : t.next}</Primary>
      </div>
    </div>
  );
};

const RankStep = ({ t, lang, ranking, moveRank, name, setName, onPrev, onSubmit, sending }) => (
  <div className="animate-fade-up">
    <div className="text-center mb-8">
      <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-3">{t.rankTitle}</h2>
      <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.rankHint}</p>
    </div>

    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-7 mb-6">
      <ul className="space-y-2" data-testid="entourage-ranking">
        {ranking.map((key, i) => {
          const d = DRIVER_BY_KEY[key];
          const nm = lang === "es" && DRIVERS_ES[key] ? DRIVERS_ES[key].name : d.name;
          return (
            <li key={key} className="flex items-center gap-3 rounded-2xl bg-white/70 border border-[var(--border)] p-3" data-testid={`entourage-rank-${key}`}>
              <span className="flex items-center justify-center h-7 w-7 rounded-full text-white text-sm font-semibold" style={{ backgroundColor: d.color }}>{i + 1}</span>
              <span className="flex-1 text-[var(--ink)]">{nm}</span>
              <div className="flex flex-col">
                <button onClick={() => moveRank(key, -1)} data-testid={`entourage-rank-up-${key}`} className="text-[var(--ink-soft)] hover:text-[var(--gold)] disabled:opacity-30" disabled={i === 0}><ChevronUp size={18} /></button>
                <button onClick={() => moveRank(key, 1)} data-testid={`entourage-rank-down-${key}`} className="text-[var(--ink-soft)] hover:text-[var(--gold)] disabled:opacity-30" disabled={i === ranking.length - 1}><ChevronDown size={18} /></button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>

    <div className="rounded-2xl bg-white/70 border border-[var(--border)] p-5 mb-6">
      <label className="block text-sm text-[var(--ink)] mb-2 font-medium">{t.nameLabel}</label>
      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="entourage-name-input" placeholder={t.namePh}
        className="w-full rounded-xl border border-[var(--border)] bg-[var(--sand)] p-3 focus:outline-none focus:border-[var(--gold)]" />
    </div>

    <div className="flex items-center justify-between mt-8">
      <Ghost onClick={onPrev} data-testid="entourage-btn-rank-prev">{t.prev}</Ghost>
      <Primary onClick={onSubmit} disabled={sending} data-testid="entourage-btn-submit">{sending ? t.sending : t.submit}</Primary>
    </div>
  </div>
);

const Done = ({ t, name, onAgain }) => (
  <div className="animate-fade-up text-center py-8" data-testid="entourage-done">
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 sm:p-12">
      <h2 className="font-serif-title text-4xl text-[var(--gold)] mb-4">{t.thanks(name)}</h2>
      <p className="text-[var(--ink)] leading-relaxed max-w-md mx-auto mb-6">{t.thanksText}</p>
      <Ghost onClick={onAgain} data-testid="entourage-btn-again">{t.respondAgain}</Ghost>
      <p className="text-xs text-[var(--ink-soft)] mt-5">{t.close}</p>
    </div>
  </div>
);

export default function DriversEntourageModule() {
  const { token } = useParams();
  const total = EXO_DRIVERS.length;
  const [lang, setLang] = useState("fr");
  const [phase, setPhase] = useState("loading"); // loading | invalid | intro | drivers | rank | done
  const [index, setIndex] = useState(0);
  const [entries, setEntries] = useState(freshEntries);
  const [ranking, setRanking] = useState(DEFAULT_RANKING);
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const t = T[lang];

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await axios.get(`${API}/drivers-entourage/proche/${token}`).then((r) => r.data);
        if (!active) return;
        if (!data.found) { setPhase("invalid"); return; }
        if (data.lang === "es") setLang("es");
        setPhase("intro");
      } catch {
        if (active) setPhase("invalid");
      }
    })();
    return () => { active = false; };
  }, [token]);

  const setCase = (driverKey, caseKey, val) =>
    setEntries((prev) => ({ ...prev, [driverKey]: { ...prev[driverKey], [caseKey]: val } }));

  const moveRank = (key, dir) =>
    setRanking((prev) => {
      const arr = [...prev];
      const i = arr.indexOf(key);
      const j = i + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });

  const nextDriver = () => { if (index < total - 1) { setIndex((i) => i + 1); } else { setPhase("rank"); } scrollTop(); };
  const prevDriver = () => { if (index > 0) { setIndex((i) => i - 1); } else { setPhase("intro"); } scrollTop(); };

  const submit = async () => {
    setSending(true);
    try {
      const out = await axios.post(`${API}/drivers-entourage/proche/${token}/submit`, {
        proche_name: name, proche_ranking: ranking, proche_entries: entries,
      }).then((r) => r.data);
      if (!out.found) { toast.error(lang === "es" ? "Enlace no encontrado" : "Lien introuvable"); setSending(false); return; }
      setPhase("done"); setSending(false); scrollTop();
    } catch {
      toast.error(lang === "es" ? "Error al enviar — inténtelo de nuevo." : "Échec de l'envoi — réessayez.");
      setSending(false);
    }
  };

  const restartForAnother = () => {
    setEntries(freshEntries());
    setRanking(DEFAULT_RANKING);
    setName("");
    setIndex(0);
    setPhase("intro");
    scrollTop();
  };

  const render = () => {
    switch (phase) {
      case "loading":
        return <div className="text-center py-20 text-[var(--ink-soft)]" data-testid="entourage-loading">{t.loading}</div>;
      case "invalid":
        return (
          <div className="text-center py-20 animate-fade-up" data-testid="entourage-invalid">
            <h1 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-3">{t.invalidTitle}</h1>
            <p className="text-[var(--ink-soft)] max-w-md mx-auto leading-relaxed">{t.invalidText}</p>
          </div>
        );
      case "intro":
        return <Intro t={t} onStart={() => { setPhase("drivers"); setIndex(0); scrollTop(); }} />;
      case "drivers": {
        const driver = EXO_DRIVERS[index];
        return <DriverStep t={t} lang={lang} driver={driver} index={index} total={total} values={entries[driver.key]} onChange={(ck, v) => setCase(driver.key, ck, v)} onPrev={prevDriver} onNext={nextDriver} />;
      }
      case "rank":
        return <RankStep t={t} lang={lang} ranking={ranking} moveRank={moveRank} name={name} setName={setName} onPrev={() => { setPhase("drivers"); setIndex(total - 1); scrollTop(); }} onSubmit={submit} sending={sending} />;
      default:
        return <Done t={t} name={name} onAgain={restartForAnother} />;
    }
  };

  return (
    <Layout>
      <div className="flex justify-end mb-4">
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>
      {render()}
    </Layout>
  );
}
